import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';

const STORAGE_KEY = 'secrets.v1';
const STORE_VERSION = 1;
const HYDRATION_TIMEOUT_MS = 1000;

export type SecretsMap = Record<string, string>;

type SecretsState = {
  version: number;
  secrets: SecretsMap;
  hasLoadedOnce: boolean;
  setSecrets: (secrets: SecretsMap) => void;
  reset: () => void;
};

export const useSecretsStore = create<SecretsState>()(
  persist(
    subscribeWithSelector((set) => ({
      version: STORE_VERSION,
      secrets: {},
      hasLoadedOnce: false,

      setSecrets: (secrets) =>
        set({
          secrets,
          hasLoadedOnce: true,
        }),

      reset: () =>
        set({
          secrets: {},
          hasLoadedOnce: false,
        }),
    })),
    {
      name: STORAGE_KEY,
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        version: state.version,
        secrets: state.secrets,
        hasLoadedOnce: state.hasLoadedOnce,
      }),
    }
  )
);

export const useSecret = (key: string): string | undefined =>
  useSecretsStore((s) => s.secrets[key]);

export const useSecrets = (): SecretsMap => useSecretsStore((s) => s.secrets);

export const useHasLoadedSecretsOnce = (): boolean =>
  useSecretsStore((s) => s.hasLoadedOnce);

export type HydrationStatus =
  | { state: 'pending' }
  | { state: 'ready' }
  | { state: 'error'; reason: 'timeout' };

// Tracks persist hydration status. A localStorage failure (quota, Safari private
// mode, corrupted JSON) flips to { state: 'error' } so callers can force a fresh
// fetch instead of silently proceeding with an empty secrets store.
export const useHydrationStatus = (): HydrationStatus => {
  const [status, setStatus] = useState<HydrationStatus>(
    useSecretsStore.persist.hasHydrated() ? { state: 'ready' } : { state: 'pending' }
  );
  useEffect(() => {
    const unsub = useSecretsStore.persist.onFinishHydration(() =>
      setStatus({ state: 'ready' })
    );
    if (useSecretsStore.persist.hasHydrated()) setStatus({ state: 'ready' });
    const fallback = setTimeout(() => {
      if (!useSecretsStore.persist.hasHydrated()) {
        console.warn(
          '[secretsStore] persist hydration timed out — localStorage may be unavailable'
        );
        setStatus({ state: 'error', reason: 'timeout' });
      }
    }, HYDRATION_TIMEOUT_MS);
    return () => {
      unsub();
      clearTimeout(fallback);
    };
  }, []);
  return status;
};

export const useHasHydratedSecrets = (): boolean =>
  useHydrationStatus().state !== 'pending';

export const getSecret = (key: string): string | undefined =>
  useSecretsStore.getState().secrets[key];

export const getSecrets = (): SecretsMap => useSecretsStore.getState().secrets;
